import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  prequalificationApi,
  type ConvertPrequalificationPayload,
  type SendPrequalificationToBuyerCrmPayload,
  type PrequalInviteListQuery,
  type PrequalInternalVerification,
  type PrequalificationDraftListQuery,
  type PrequalificationListQuery,
  type PrequalificationStatus,
  type SendPrequalInvitePayload,
} from "@/api/prequalification";
import { partnerBrandsApi } from "@/api/partnerBrands";
import { partnerBrandKeys } from "@/hooks/partners/partnerBrandKeys";
import { partiesKeys } from "@/hooks/parties/partiesKeys";
import { prequalKeys } from "./prequalKeys";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";

// Deal Flow → Prequalification hooks (leads-template clone). Every mutation
// invalidates the prequalKeys.all prefix (stale-after-save convention).

export function usePrequalificationList(query: PrequalificationListQuery) {
  return useQuery({
    queryKey: prequalKeys.list({ ...query }),
    queryFn: () => prequalificationApi.list(query),
  });
}

export function usePrequalificationStats(query: PrequalificationListQuery) {
  return useQuery({
    queryKey: prequalKeys.stats({ ...query }),
    queryFn: () => prequalificationApi.stats(query),
  });
}

export function usePrequalificationDetail(id: string) {
  return useQuery({
    queryKey: prequalKeys.detail(id),
    queryFn: () => prequalificationApi.get(id),
    enabled: !!id,
  });
}

export function usePrequalificationActivity(id: string) {
  return useQuery({
    queryKey: prequalKeys.activity(id),
    queryFn: () => prequalificationApi.activity(id),
    enabled: !!id,
  });
}

function useInvalidatePrequal() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: prequalKeys.all });
}

export function useAssignPrequalification(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (userId: string | null) => prequalificationApi.assign(id, userId),
    onSuccess: () => invalidate(),
  });
}

export function useChangePrequalificationStatus(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (vars: { status: PrequalificationStatus; reason?: string }) =>
      prequalificationApi.changeStatus(id, vars.status, vars.reason),
    onSuccess: () => invalidate(),
  });
}

/**
 * Re-render this submission's documents.
 *
 * Invalidates the whole prequalification prefix, which is what makes the
 * change visible everywhere at once: the detail panel's document list, the
 * activity feed that now carries the regeneration row, and — the one a
 * narrower key would miss — the LIST, whose DOCUMENTS column and
 * "Missing documents" filter both read from the same documents the run just
 * wrote.
 */
export function useRegeneratePrequalificationDocuments(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: () => prequalificationApi.regenerateDocuments(id),
    onSuccess: () => invalidate(),
  });
}

/**
 * What a resend would send, for the preview modal.
 *
 * Lazy — `enabled` is the caller's `open` flag — because composing a draft
 * downloads every stored PDF server-side to size the attachment list honestly.
 * A detail page that fetched it on render would pay that on every open of every
 * submission for a control most visits never touch.
 */
export function usePrequalificationResendDraft(id: string, enabled: boolean) {
  return useQuery({
    queryKey: prequalKeys.resendDraft(id),
    queryFn: () => prequalificationApi.resendDraft(id),
    enabled: !!id && enabled,
  });
}

/**
 * Send the applicant their documents again.
 *
 * Invalidates the whole prefix: the send stamps `emailedAtUtc` (the detail
 * header and the LIST both read it) and writes an activity row.
 */
export function useResendPrequalificationDocuments(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (body?: { subject?: string; body?: string }) =>
      prequalificationApi.resend(id, body),
    onSuccess: () => invalidate(),
  });
}

/**
 * Record whether staff have checked an internal buyer's asserted relationship.
 *
 * Invalidates the whole prequalification prefix — the detail panel's badge, the
 * ACTIVITY feed (which is the whole audit surface for this state, so the row the
 * call just wrote is the point), and the LIST, whose badge column and
 * verification filter both read it.
 *
 * 🔴 The server is deliberately NOT idempotent here: setting the state it
 * already holds writes a fresh row and a fresh timestamp, because "somebody
 * looked again today" answers "how recently did anyone check" and a re-assertion
 * that did nothing would answer it wrongly. So the client must not short-circuit
 * a same-value click either.
 */
export function useSetInternalVerification(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (state: PrequalInternalVerification) =>
      prequalificationApi.setInternalVerification(id, state),
    onSuccess: () => invalidate(),
  });
}

export function useArchivePrequalification(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: () => prequalificationApi.archive(id),
    onSuccess: () => invalidate(),
  });
}

export function useUnarchivePrequalification(id: string) {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: () => prequalificationApi.unarchive(id),
    onSuccess: () => invalidate(),
  });
}

/**
 * Convert the submission into a Party.
 *
 * Invalidates the PARTIES prefix as well as the prequalification one: a
 * conversion mints a row the Borrowers / Sellers registries read, and without
 * this the operator lands on a party that their own registry list does not show
 * for another five minutes (the client's 5-minute staleTime with no
 * refetch-on-focus).
 */
export function useConvertPrequalification(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (body?: ConvertPrequalificationPayload) =>
      prequalificationApi.convert(id, body),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: partiesKeys.all });
    },
  });
}

// ── Send to the assignment queue (Buyer CRM) ────────────────────────────────

/** The send dialog's state and the detail page's link-back. Buyer role only —
 *  a seller is never sent to the Buyer CRM, so the read is not issued for one. */
export function usePrequalificationBuyerCrm(id: string, enabled: boolean) {
  return useQuery({
    queryKey: prequalKeys.buyerCrm(id),
    queryFn: () => prequalificationApi.buyerCrm(id),
    enabled: !!id && enabled,
  });
}

/** Sends (or links) the completion. Invalidates every prequalification read —
 *  the detail state, the list column and the stats all change — and the
 *  Buyer CRM, where a buyer was created or linked. Runs on a 409 too: a
 *  "somebody got there first" answer means the cached state is stale. */
export function useSendPrequalificationToBuyerCrm(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (body?: SendPrequalificationToBuyerCrmPayload) =>
      prequalificationApi.sendToBuyerCrm(id, body),
    onSettled: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

// ── Tracked invites ─────────────────────────────────────────────────────────

/**
 * Tracked invites. The whole `query` — status, scope, partner site, `q`, `sort`,
 * paging — rides both the key and the request, so search and sort change the
 * cache identity rather than re-rendering the previous page against a stale
 * entry. Blank values are dropped by the client's own URL builder, which is what
 * makes an unset `q` the list exactly as it was before search existed.
 */
export function usePrequalificationInvites(query: PrequalInviteListQuery) {
  return useQuery({
    queryKey: prequalKeys.invites({ ...query }),
    queryFn: () => prequalificationApi.listInvites(query),
  });
}

export function useSendPrequalificationInvite() {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (body: SendPrequalInvitePayload) => prequalificationApi.sendInvite(body),
    onSuccess: () => invalidate(),
  });
}

export function useResendPrequalificationInvite() {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (id: string) => prequalificationApi.resendInvite(id),
    onSuccess: () => invalidate(),
  });
}

export function useRevokePrequalificationInvite() {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (id: string) => prequalificationApi.revokeInvite(id),
    onSuccess: () => invalidate(),
  });
}

// ── In-progress drafts ──────────────────────────────────────────────────────

/**
 * Applications that were started and not finished — a PARALLEL source to the
 * submissions list, with its own endpoint, because a draft is neither a
 * submission nor an invite and no filter on that list could ever return one.
 *
 * Read-only: nothing on this section mutates, so there is no invalidation to
 * register here. The drafts key sits under `prequalKeys.all`, so the existing
 * mutations already cover it.
 *
 * `q` and `sort` ride the key with the rest of the query, for the same reason
 * they do on the invites list.
 */
export function usePrequalificationDrafts(query: PrequalificationDraftListQuery) {
  return useQuery({
    queryKey: prequalKeys.drafts({ ...query }),
    queryFn: () => prequalificationApi.listDrafts(query),
  });
}

/**
 * Start an in-office prequalification: the client is with a staff member, who
 * will drive the form.
 *
 * <b>This is NOT the anonymous create.</b> It mints a suppressed invite so the
 * draft is INVITED rather than organic — Surface 1's "no invite token means
 * organic" rule, applied to a signed-in caller, would produce a draft with a
 * null sender that is invisible on the initiator's own view, surfaced to staff
 * as an abandoned organic lead, and with no invite to mark Started. The wrong
 * outcome reached by obeying a stated rule, which is why it survives review.
 *
 * Nothing is emailed — the client is in the room.
 *
 * The three lists move together (a draft is minted and its invite is stamped),
 * so this invalidates the whole prefix rather than one of them.
 */
export function useStartInOfficePrequalDraft() {
  const invalidate = useInvalidatePrequal();
  return useMutation({
    mutationFn: (body: SendPrequalInvitePayload) =>
      prequalificationApi.startInOfficeDraft(body),
    onSuccess: () => invalidate(),
  });
}

/**
 * Active partner brands, for the invite site picker and the submissions
 * partner filter. Registers under the shared `partnerBrandKeys` cache slot.
 *
 * The brand list is gated on `Partners.ManageBrands` — held by C0 / C1 / C5
 * through `RolePermissionMap`'s `PrequalificationGrants` bundle, plus ADMIN via
 * `Permissions.All` (pinned by `PartnerBrandManageGrantsTests`) — while sending
 * an invite only needs `Prequalification.Manage`. The two sets are not
 * identical, so callers pass `enabled: false` when the user lacks the brands
 * permission and fall back to the Phoenix-native choice rather than firing a
 * doomed 403.
 */
export function usePartnerBrandOptions(enabled: boolean) {
  return useQuery({
    queryKey: partnerBrandKeys.list(),
    queryFn: () => partnerBrandsApi.list(),
    enabled,
  });
}
